import { ApiOutlined, CopyOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Alert, App, Button, Checkbox, Input, List, Modal, Space, Tag, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { listApiKeys, mintApiKey, revokeApiKey, getApiEntitlements, ApiKeyRow, ApiEntitlements } from '@/api/protomia';

// Personal API access — platform-scoped keys minted by THIS platform's account_center, reached
// through the Protomia gateway (/api/account/keys → loopback account_center). The raw key is
// shown exactly once at mint time; the list only ever carries prefix + label + last-used.
// Minting is default-deny: an account without the api_access grant gets the server's own
// message back verbatim (it says to ask an administrator).

interface ApiAccessProps {
    open: boolean;
    onClose: () => void;
}

const ApiAccess: React.FC<ApiAccessProps> = ({ open, onClose }) => {
    const { message, modal } = App.useApp();
    const { t } = useTranslation('common');
    const [keys, setKeys] = useState<ApiKeyRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [label, setLabel] = useState('');
    const [freshKey, setFreshKey] = useState<string | null>(null);
    const [denied, setDenied] = useState<string | null>(null);
    const [ent, setEnt] = useState<ApiEntitlements>({ model: false, mcp: false });
    const [scopes, setScopes] = useState<string[]>([]);

    const baseUrl = ((import.meta as any).env?.VITE_PROTOMIA_URL || '') as string;

    const load = async () => {
        setLoading(true);
        try {
            const e = await getApiEntitlements();
            setEnt(e);
            setScopes(prev => {
                const allowed = [e.model ? 'model' : '', e.mcp ? 'mcp' : ''].filter(Boolean);
                const kept = prev.filter(x => allowed.includes(x));
                return kept.length ? kept : allowed;
            });
            if (!e.model && !e.mcp) setDenied(t('api_access.denied'));
            const rows: ApiKeyRow[] = await listApiKeys();
            setKeys(rows.filter(k => !k.revoked_at));
        } catch (e: any) {
            const status = e?.response?.status;
            if (status === 503) setDenied(t('api_access.not_available'));
            else message.error(t('api_access.load_failed'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            setFreshKey(null);
            setDenied(null);
            load();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const mint = async () => {
        setLoading(true);
        try {
            const res = await mintApiKey(label.trim(), scopes.join(','));
            const raw: string | undefined = res?.key;
            if (raw) {
                setFreshKey(raw);
                setLabel('');
                await load();
            } else {
                message.error(t('api_access.mint_failed'));
            }
        } catch (e: any) {
            const status = e?.response?.status;
            const serverMsg = e?.response?.data?.message;
            if (status === 403) setDenied(serverMsg || t('api_access.denied'));
            else message.error(serverMsg || t('api_access.mint_failed'));
        } finally {
            setLoading(false);
        }
    };

    const revoke = (row: ApiKeyRow) => {
        modal.confirm({
            title: t('api_access.revoke_confirm_title'),
            content: `${row.prefix}…  ${row.label || ''}`,
            okText: t('app_setting.yes'),
            cancelText: t('app_setting.no'),
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    await revokeApiKey(row.id);
                    message.success(t('api_access.revoked'));
                    await load();
                } catch {
                    message.error(t('api_access.revoke_failed'));
                }
            },
        });
    };

    const copy = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            message.success(t('api_access.copied'));
        } catch {
            message.warning(t('api_access.copy_manually'));
        }
    };

    const endpoint = `${window.location.origin}${baseUrl}/v1`;

    return (
        <Modal
            title={<span><ApiOutlined /> <span className='ml-2'>{t('api_access.title')}</span></span>}
            open={open}
            onCancel={onClose}
            footer={null}
            width={560}
        >
            <Space orientation='vertical' className='w-full' size='middle'>
                <Typography.Paragraph type='secondary' className='mb-0'>
                    {t('api_access.intro')}
                </Typography.Paragraph>

                {denied && <Alert type='warning' showIcon message={denied} />}

                {freshKey && (
                    <Alert
                        type='success'
                        showIcon
                        message={t('api_access.fresh_title')}
                        description={
                            <Space orientation='vertical' className='w-full'>
                                <Typography.Text type='warning'>{t('api_access.fresh_notice')}</Typography.Text>
                                <Space.Compact style={{ width: '100%' }}>
                                    <Input value={freshKey} readOnly style={{ fontFamily: 'monospace' }} />
                                    <Button icon={<CopyOutlined />} onClick={() => copy(freshKey)} />
                                </Space.Compact>
                            </Space>
                        }
                    />
                )}

                {!denied && (
                    <Checkbox.Group
                        value={scopes}
                        onChange={v => setScopes(v as string[])}
                        options={[
                            { label: t('api_access.scope_model'), value: 'model', disabled: !ent.model },
                            { label: t('api_access.scope_mcp'), value: 'mcp', disabled: !ent.mcp },
                        ]}
                    />
                )}
                {!denied && (
                    <Space.Compact style={{ width: '100%' }}>
                        <Input
                            placeholder={t('api_access.label_placeholder')}
                            value={label}
                            maxLength={64}
                            onChange={e => setLabel(e.target.value)}
                            onPressEnter={mint}
                        />
                        <Button type='primary' icon={<PlusOutlined />} loading={loading} disabled={scopes.length === 0} onClick={mint}>
                            {t('api_access.mint')}
                        </Button>
                    </Space.Compact>
                )}

                <List
                    size='small'
                    loading={loading}
                    locale={{ emptyText: t('api_access.empty') }}
                    dataSource={keys}
                    renderItem={row => (
                        <List.Item
                            actions={[
                                <Button key='rm' type='text' danger icon={<DeleteOutlined />} onClick={() => revoke(row)}>
                                    {t('api_access.revoke')}
                                </Button>,
                            ]}
                        >
                            <Space>
                                <Tag style={{ fontFamily: 'monospace' }}>{row.prefix}…</Tag>
                                {(row.scopes || '').split(',').filter(Boolean).map(sc => (
                                    <Tag key={sc} color={sc === 'mcp' ? 'purple' : 'blue'}>{sc}</Tag>
                                ))}
                                <span>{row.label || <Typography.Text type='secondary'>{t('api_access.unlabelled')}</Typography.Text>}</span>
                                <Typography.Text type='secondary' style={{ fontSize: 12 }}>
                                    {row.last_used_at
                                        ? `${t('api_access.last_used')} ${new Date(row.last_used_at).toLocaleString()}`
                                        : t('api_access.never_used')}
                                </Typography.Text>
                            </Space>
                        </List.Item>
                    )}
                />

                <Typography.Paragraph type='secondary' className='mb-0' style={{ fontSize: 12 }}>
                    <b>{t('api_access.tier_a')}</b> — {t('api_access.howto')}
                    <pre style={{ marginTop: 8, marginBottom: 0, whiteSpace: 'pre-wrap' }}>
{`base_url  ${endpoint}
api_key   sk-…  (${t('api_access.your_key')})

model     qwen3.8-27b-local        # ${t('api_access.tier_default')}
model     qwen3.8-27b-local-long   # ${t('api_access.tier_long')}`}
                    </pre>
                </Typography.Paragraph>
                <Typography.Paragraph type='secondary' className='mb-0' style={{ fontSize: 12 }}>
                    <b>{t('api_access.tier_b')}</b> — {t('api_access.tier_b_howto')}
                    <pre style={{ marginTop: 8, marginBottom: 0, whiteSpace: 'pre-wrap' }}>
{`# opencode  (~/.config/opencode/opencode.json → "mcp")
"protomia": {"type": "remote", "url": "${window.location.origin}${baseUrl}/mcp/pods",
                  "headers": {"Authorization": "Bearer sk-…"}, "enabled": true}

# Claude Code
claude mcp add --transport http protomia ${window.location.origin}${baseUrl}/mcp/pods \\
  --header "Authorization: Bearer sk-…"`}
                    </pre>
                    {t('api_access.tier_b_note')}
                </Typography.Paragraph>
            </Space>
        </Modal>
    );
};

export default ApiAccess;
